import { useOrganizations } from '@contexts/organizations-provider';
import { useHasCompanyDocuments } from '@features/documents/hooks/use-has-company-documents/useHasCompanyDocuments';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import * as React from 'react';

import type { CompanyDocumentsGuardProps } from './CompanyDocumentsGuard.types';

/**
 * Route guard that blocks access to the Company Documents page when
 * the organization has no documents (totalCount === 0 or API error).
 *
 * Shows a loader while the availability check is in-flight, and
 * renders a not-found feedback when no documents exist.
 *
 * Must be placed INSIDE PermissionGuard (permissions already validated).
 */
const CompanyDocumentsGuard = React.memo(
  ({ children }: Readonly<CompanyDocumentsGuardProps>): React.ReactElement => {
    const { selectedOrganization } = useOrganizations();

    const { hasDocuments, isLoading } = useHasCompanyDocuments({
      organizationId: selectedOrganization?.id,
    });

    if (isLoading) {
      return <ContentLoader />;
    }

    if (!hasDocuments) {
      return <InlineFeedback type="not-found" />;
    }

    return <>{children}</>;
  }
);

CompanyDocumentsGuard.displayName = 'CompanyDocumentsGuard';

export { CompanyDocumentsGuard };
